import { useState } from 'react';
import { FiX, FiCheckCircle, FiAlertCircle, FiCalendar, FiClock } from 'react-icons/fi';
import { BsTicketPerforated } from 'react-icons/bs';
import { Button } from '../ui/Button';
import { checkInTicket, type EventItem } from '../../api/events';
import { formatTime } from '../../utils/eventDate';
import { extractApiError } from '../../utils/apiError';
import { getEventCoverImage } from '../../api/events';

interface EventCheckInModalProps {
  event: EventItem;
  isOpen: boolean;
  onClose: () => void;
}

type ModalView = 'form' | 'success' | 'error';

export function EventCheckInModal({ event, isOpen, onClose }: EventCheckInModalProps) {
  const [checkInCode, setCheckInCode] = useState('');
  const [view, setView] = useState<ModalView>('form');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [checkedInAt, setCheckedInAt] = useState<string>('');
  const [ticketTypeName, setTicketTypeName] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');

  if (!isOpen) return null;

  const coverImage = getEventCoverImage(event);
  const title = event.event_title || 'Untitled Event';
  const description = event.info ?? '';
  const startTime = event.start ? formatTime(event.start, event.timezone, true) : '';
  const endTime = event.end ? formatTime(event.end, event.timezone, true) : '';
  const timeRange = [startTime, endTime].filter(Boolean).join(' – ');

  const handleClose = () => {
    setCheckInCode('');
    setView('form');
    setErrorMessage('');
    setCheckedInAt('');
    setTicketTypeName('');
    setVerificationCode('');
    onClose();
  };

  const handleCheckIn = async () => {
    const trimmed = checkInCode.trim();
    if (!trimmed) return;

    setIsLoading(true);
    setErrorMessage('');

    try {
      const result = await checkInTicket(event.id, trimmed);
      if (result.success === false || result.error) {
        setErrorMessage(result.error || result.message || 'Check-in failed. Please try again.');
        setView('error');
      } else {
        setCheckedInAt(result.check_in_time ?? result.checked_in_at ?? new Date().toISOString());
        setTicketTypeName(result.ticket_type_name ?? '');
        setVerificationCode(result.verification_code ?? trimmed.toUpperCase());
        setView('success');
      }
    } catch (err: unknown) {
      setErrorMessage(extractApiError(err, 'Invalid check-in code or ticket not found.'));
      setView('error');
    } finally {
      setIsLoading(false);
    }
  };

  const checkedInDate = checkedInAt
    ? new Date(checkedInAt).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';
  const checkedInTime = checkedInAt
    ? new Date(checkedInAt).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

      {/* Panel */}
      <div className="relative w-full sm:max-w-md max-h-[calc(100dvh-2rem)] bg-zinc-950 border border-white/10 rounded-3xl shadow-2xl overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 pt-5 pb-4 border-b border-white/8">
          <h2 className="text-white text-lg font-semibold">
            {view === 'success' ? 'Check-in Confirmed' : 'Check In to this event'}
          </h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/8 text-zinc-400 hover:text-white transition-colors"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="px-4 sm:px-5 py-5 space-y-4">
          {/* ── Success View ── */}
          {view === 'success' && (
            <div className="flex flex-col items-center gap-5 py-4">
              {/* Green checkmark */}
              <div className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center shadow-lg shadow-green-500/30">
                <FiCheckCircle size={40} className="text-white" />
              </div>

              {/* Verification code badge */}
              <div className="px-3 py-1 rounded-full bg-zinc-800 border border-white/10">
                <span className="text-white text-[13px] font-medium">Code: {verificationCode}</span>
              </div>

              <div className="text-center">
                <h3 className="text-white text-2xl font-bold">Checked in</h3>
                <p className="text-zinc-400 text-sm mt-1">
                  {ticketTypeName ? ticketTypeName : 'Guest entry confirmed'}
                </p>
              </div>

              {/* Details card */}
              <div className="w-full rounded-2xl bg-zinc-900 border border-white/8 divide-y divide-white/8">
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2 text-zinc-400">
                    <FiCalendar size={15} className="text-blue-400" />
                    <span className="text-sm">Event</span>
                  </div>
                  <span className="text-white text-sm font-medium text-right max-w-[55%] truncate">
                    {title}
                  </span>
                </div>
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2 text-zinc-400">
                    <FiCalendar size={15} className="text-blue-400" />
                    <span className="text-sm">Date</span>
                  </div>
                  <span className="text-white text-sm font-medium">{checkedInDate}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2 text-zinc-400">
                    <FiClock size={15} className="text-blue-400" />
                    <span className="text-sm">Time</span>
                  </div>
                  <span className="text-white text-sm font-medium">{checkedInTime}</span>
                </div>
              </div>

              <Button onClick={handleClose} className="w-full rounded-full" size="lg">
                Done
              </Button>
            </div>
          )}

          {/* ── Error View ── */}
          {view === 'error' && (
            <div className="flex flex-col items-center gap-5 py-4">
              <div className="w-20 h-20 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
                <FiAlertCircle size={38} className="text-red-400" />
              </div>
              <div className="text-center">
                <h3 className="text-white text-xl font-bold">Check-in Failed</h3>
                <p className="text-zinc-400 text-sm mt-2 max-w-xs mx-auto">{errorMessage}</p>
              </div>
              <div className="flex flex-col gap-3 w-full">
                <Button
                  onClick={() => {
                    setView('form');
                    setCheckInCode('');
                  }}
                  className="w-full rounded-full"
                  size="lg"
                >
                  Try Again
                </Button>
                <button
                  onClick={handleClose}
                  className="text-zinc-500 text-sm hover:text-zinc-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* ── Form View ── */}
          {view === 'form' && (
            <>
              {/* Event summary card */}
              <div className="flex items-center gap-3 p-3 rounded-2xl bg-zinc-900 border border-white/8">
                <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 bg-zinc-800">
                  {coverImage ? (
                    <img src={coverImage} alt={title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-800">
                      <FiCalendar size={20} className="text-zinc-500" />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-white font-semibold text-[15px] leading-snug truncate">
                    {title}
                  </p>
                  {description && (
                    <p className="text-zinc-500 text-[12px] mt-0.5 line-clamp-1">{description}</p>
                  )}
                  {timeRange && <p className="text-zinc-500 text-[12px] mt-0.5">{timeRange}</p>}
                </div>
              </div>

              {/* Check-in code input */}
              <div className="space-y-2">
                <label className="text-zinc-300 text-sm font-medium block">Check-in Code</label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-400">
                    <BsTicketPerforated size={18} />
                  </div>
                  <input
                    type="text"
                    value={checkInCode}
                    onChange={(e) => setCheckInCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && !isLoading && checkInCode.trim() && handleCheckIn()
                    }
                    placeholder="Enter check-in code (e.g. ON9728)"
                    className="w-full pl-10 pr-4 py-3.5 rounded-2xl bg-zinc-900 border border-white/10 text-white placeholder:text-zinc-600 focus:outline-none focus:border-blue-500/60 text-sm transition-colors"
                    autoFocus
                  />
                </div>
              </div>

              {/* Check in button */}
              <Button
                onClick={handleCheckIn}
                disabled={!checkInCode.trim() || isLoading}
                className="w-full rounded-full bg-blue-600 hover:bg-blue-500 text-white border-0"
                size="lg"
              >
                {isLoading ? 'Checking in…' : 'Check in'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
